import { z } from 'zod'

// Enums
export const OrientationSchema = z.enum([
  'north',
  'northeast',
  'east',
  'southeast',
  'south',
  'southwest',
  'west',
  'northwest',
])

export const RoofOrientationSchema = z.enum([
  'north',
  'northeast',
  'east',
  'southeast',
  'south',
  'southwest',
  'west',
  'northwest',
  'horizontal',
])

export const BorderingTypeSchema = z.enum(['toSoil', 'toOutsideAir', 'toUnheated'])

export const HeatingGeneratorTypeSchema = z.enum([
  'boiler',
  'standardBoiler',
  'lowTemperatureBoiler',
  'condensingBoiler',
  'biomassBoiler',
  'heatPump',
  'groundSourceHeatPump',
  'groundWaterWaterHeatPump',
  'airWaterHeatPumpMonovalent',
  'airWaterHeatPumpBivalent',
  'airAirHeatPumpMonovalent',
  'airAirHeatPumpBivalent',
  'electricPanel',
  'districtHeating',
  'vrf',
  'chp',
  'stove',
  'exhaustWaterHeatPump',
  'exhaustAirHeatPump',
  'fuelCell',
  'solarThermalSystem',
])

export const DHWGeneratorTypeSchema = z.enum([
  'combinedWithHeating',
  'boiler',
  'standardBoiler',
  'lowTemperatureBoiler',
  'condensingBoiler',
  'biomassBoiler',
  'heatPump',
  'groundSourceHeatPump',
  'groundWaterWaterHeatPump',
  'airWaterHeatPumpMonovalent',
  'airWaterHeatPumpBivalent',
  'exhaustAirAirWaterHeatPump',
  'districtHeating',
  'chp',
  'electricFlowHeater',
  'gasFlowHeater',
  'dhwStation',
  'solarThermalSystem',
])

export const EnergySourceSchema = z.enum([
  'heatingOil',
  'bioOil',
  'gas',
  'bioGas',
  'liquefiedGas',
  'firewood',
  'pellets',
  'biomass',
  'electricity',
  'districtHeatHeatPlant',
  'districtHeatHeatPlantFossil',
  'districtHeatHeatPlantRenewable',
  'districtHeatCHPFossil',
  'districtHeatCHPRenewable',
  'mineralCoal',
  'brownCoal',
  'blackCoal',
  'solar',
  'coke',
])

export const HeatingTransferTypeSchema = z.enum([
  'radiators',
  'floorHeating',
  'fanCoil',
  'wallHeating',
])

export const VentilationTypeSchema = z.enum(['naturalVentilation', 'other'])

// Current State Description
export const CurrentStateDescriptionSchema = z.object({
  lowerCompletion: z.string().optional(),
  basementEntrance: z.string().optional(),
  exteriorWalls: z.string().optional(),
  windows: z.string().optional(),
  upperCompletion: z.string().optional(),
  heating: z.string().optional(),
  heatingTransfer: z.string().optional(),
  drinkingHotWater: z.string().optional(),
  ventilation: z.string().optional(),
})

// Building Information
export const BuildingInformationSchema = z.object({
  heatedVolumeInCbm: z.number().gt(0),
  usableSpaceInSqm: z.number().gt(0),
  constructionYear: z.number().int().min(1500).max(2026),
  numberOfResidentialUnits: z.number().int().gt(0),
  numberOfFloors: z.number().int().gt(0),
  meanFloorHeightInCm: z.number().gt(0),
})

// Energetic Values
export const EnergeticValuesSchema = z.object({
  finalEnergyDemandInKwh: z.number().min(0),
  auxEnergyDemandInKwh: z.number().min(0),
  energyConsumptionInKwh: z.number().min(0),
  primaryEnergyDemandInKwh: z.number().min(0).optional(),
  transmissionHeatLossInWPerK: z.number().min(0).optional(),
  equivalentCo2EmissionsInTons: z.number().min(0).optional(),
})

// Component Schemas
export const ComponentWithOrientationSchema = z.object({
  uValue: z.number().gt(0),
  areaInSqm: z.number().gt(0),
  orientation: OrientationSchema,
})

export const ComponentWithRoofOrientationSchema = z.object({
  uValue: z.number().gt(0),
  areaInSqm: z.number().gt(0),
  orientation: RoofOrientationSchema,
})

export const ComponentWithoutOrientationSchema = z.object({
  uValue: z.number().gt(0),
  areaInSqm: z.number().gt(0),
})

export const FloorPlateComponentSchema = z.object({
  uValue: z.number().gt(0),
  areaInSqm: z.number().gt(0),
  borderingType: z.enum(['toSoil', 'toOutsideAir']).optional(),
})

export const BasementExteriorWallComponentSchema = z.discriminatedUnion('borderingType', [
  z.object({
    uValue: z.number().gt(0),
    areaInSqm: z.number().gt(0),
    borderingType: z.literal('toOutsideAir'),
    orientation: OrientationSchema,
  }),
  z.object({
    uValue: z.number().gt(0),
    areaInSqm: z.number().gt(0),
    borderingType: z.literal('toSoil'),
    orientation: OrientationSchema.optional(),
  }),
  z.object({
    uValue: z.number().gt(0),
    areaInSqm: z.number().gt(0),
    borderingType: z.literal('toUnheated'),
    orientation: OrientationSchema.optional(),
  }),
])

// Substance
export const SubstanceSchema = z.object({
  exteriorWallComponents: z.array(ComponentWithOrientationSchema),
  roofComponents: z.array(ComponentWithRoofOrientationSchema),
  topFloorCeilingComponents: z.array(ComponentWithoutOrientationSchema),
  basementCeilingComponents: z.array(ComponentWithoutOrientationSchema),
  floorPlateComponents: z.array(FloorPlateComponentSchema),
  basementExteriorWallComponents: z.array(BasementExteriorWallComponentSchema),
  windowComponents: z.array(ComponentWithOrientationSchema),
  doorComponents: z.array(ComponentWithOrientationSchema),
  roofWindowComponents: z.array(ComponentWithRoofOrientationSchema),
})

// System Technology
export const HeatingGenerationSchema = z.object({
  generatorType: HeatingGeneratorTypeSchema,
  energySource: EnergySourceSchema,
  constructionYear: z.number().int().min(1500).max(2026),
  coverageInPct: z.number().gt(0).max(100),
  collectorAreaInSqm: z.number().optional(),
})

export const DrinkingHotWaterGenerationSchema = z.object({
  generatorType: DHWGeneratorTypeSchema,
  energySource: EnergySourceSchema,
  constructionYear: z.number().int().min(1500).max(2026),
  coverageInPct: z.number().gt(0).max(100),
  collectorAreaInSqm: z.number().optional(),
})

export const HeatingTransferSchema = z.object({
  type: HeatingTransferTypeSchema,
})

export const VentilationSystemSchema = z.object({
  ventilationType: VentilationTypeSchema,
  heatRecoveryRateInPct: z.number().min(0).max(100).optional(),
})

export const SystemTechnologySchema = z.object({
  heatingGeneration: z.array(HeatingGenerationSchema),
  drinkingHotWaterGeneration: z.array(DrinkingHotWaterGenerationSchema),
  heatingTransfer: z.array(HeatingTransferSchema),
  ventilationSystem: z.array(VentilationSystemSchema).optional(),
})

// Main Schema
export const ISFPExtractedDataSchema = z.object({
  currentStateDescription: CurrentStateDescriptionSchema,
  buildingInformation: BuildingInformationSchema,
  energeticValues: EnergeticValuesSchema,
  substance: SubstanceSchema,
  systemTechnology: SystemTechnologySchema,
})

// TypeScript Types (derived from Zod schemas)
export type Orientation = z.infer<typeof OrientationSchema>
export type RoofOrientation = z.infer<typeof RoofOrientationSchema>
export type BorderingType = z.infer<typeof BorderingTypeSchema>
export type HeatingGeneratorType = z.infer<typeof HeatingGeneratorTypeSchema>
export type DHWGeneratorType = z.infer<typeof DHWGeneratorTypeSchema>
export type EnergySource = z.infer<typeof EnergySourceSchema>
export type HeatingTransferType = z.infer<typeof HeatingTransferTypeSchema>
export type VentilationType = z.infer<typeof VentilationTypeSchema>

export type CurrentStateDescription = z.infer<typeof CurrentStateDescriptionSchema>
export type BuildingInformation = z.infer<typeof BuildingInformationSchema>
export type EnergeticValues = z.infer<typeof EnergeticValuesSchema>
export type ComponentWithOrientation = z.infer<typeof ComponentWithOrientationSchema>
export type ComponentWithRoofOrientation = z.infer<typeof ComponentWithRoofOrientationSchema>
export type ComponentWithoutOrientation = z.infer<typeof ComponentWithoutOrientationSchema>
export type FloorPlateComponent = z.infer<typeof FloorPlateComponentSchema>
export type BasementExteriorWallComponent = z.infer<typeof BasementExteriorWallComponentSchema>
export type Substance = z.infer<typeof SubstanceSchema>
export type HeatingGeneration = z.infer<typeof HeatingGenerationSchema>
export type DrinkingHotWaterGeneration = z.infer<typeof DrinkingHotWaterGenerationSchema>
export type HeatingTransfer = z.infer<typeof HeatingTransferSchema>
export type VentilationSystem = z.infer<typeof VentilationSystemSchema>
export type SystemTechnology = z.infer<typeof SystemTechnologySchema>
export type ISFPExtractedData = z.infer<typeof ISFPExtractedDataSchema>
